import { gsap} from 'gsap';
import { BLEND_MODES } from 'pixi.js';
import { ISymbolAnimationConf, ReelBaseSymbol } from '../../../core/reel/symbol/ReelBaseSymbol';
import { Helper } from '../../../core/utils/Helper';
import { SanSymbolData } from './SanSymbolData';
import { SanQuentin2 } from '../../SanQuentin2';
import { MultSequence } from '../../proto/MultSequence';
import { SanEngineManager } from '../SanEngineManager';
import { ReelSymbolStates } from '../../../core/reel/symbol/ReelSpriteSymbolArea';
import { SimpleSpriteSwap } from '../../modules/symbol/SimpleSpriteSwap';
import { SymbolData } from '../../../core/reel/symbol/config/SymbolData';
import { ISymbolConfig } from '../../../core/reel/symbol/config/IReelSymbolStruct';
import { Logger } from '@nolimitcity/slot-launcher/bin/utils/Logger';
import { SymbolMultiplyCounter } from '../../modules/symbol/SymbolMultiplyCounter';
import { OverlayItem } from '../../component/OverlayAnimation';
import { SpinePlayer } from '../../../core/displayobject/SpinePlayer';

export enum WinShowStates {
    GLOW,
    IDLE,
    BIGWIN_START,
    WIN
}

export class SanWinSymbol extends ReelBaseSymbol {

    private seq_glows: MultSequence;
    protected multies: SymbolMultiplyCounter;

    public get symbolHeight(): number {
        return SanSymbolData.fetchSymHeightSize(this.sizeType.value);
    }

    public get symbolWidth(): number {
        return SanSymbolData.DEFAULT_DIM.WIDTH;
    }

    public get symbolSizeY(): number {
        return (this.symbolHeight * this.scale.y);
    }

    public get symbolSizeX(): number {
        return (this.symbolWidth * this.scale.x);
    }

    get glowSizeY(): number {
        switch (this.sizeType.value) {
            case 1:
                return 0.25;
            case 2:
                return 0.5;
            case 4:
                return 1;
            default:
                return 1;
        }
    }

    changeState(newStateConfig: ISymbolAnimationConf): gsap.core.Timeline | undefined { return undefined };

    public setSymbol(symName: string): void {

        let newConfig: ISymbolConfig | undefined = SymbolData.getConfig(symName);

        if (!newConfig || newConfig.NAME == "") { Logger.warn("Couldn't find symbol: " + symName); return; }

        this.symbolConfig = newConfig;

        const TEXTURES: PIXI.Texture[] | undefined = SymbolData.getTextures(this.symbolConfig.NAME, this.state.value, this.sizeType.value);

        if (TEXTURES){
            this._mainSprite.setTexture(TEXTURES[0]);
        }

        this.setPosID(this.posID[0]);
    }

    constructor(ID: number, symName: string, state: string = ReelSymbolStates.NORMAL, size: number = 4) {
        super(ID, state, size);
        this.symbolConfig = SanSymbolData.getConfig(symName);
        this.init();
    }

    private init(): void {
        this._mainSprite = this.addChild(new SimpleSpriteSwap(SanSymbolData.getTextures(this.symbolConfig.NAME, this.state.value, this.sizeType.value)![0]));
        this._mainSprite.visible = false;
        
        this.createWinFlash();
        SanQuentin2.overlay.addChild(this);
        
        this.visible = false;
    }

    private createWinFlash(): void {

        if (!this.seq_glows) {
            this.seq_glows =  this.addChild(create());
        }

        function create(): MultSequence {
            const temp: MultSequence = new MultSequence([
                "Blink_Bar_Tiny",
                "Blink_Bar_Small",
                "Blink_Bar_Medium",
                "Blink_Bar_High",
            ]);
            temp.anchor.set(0.5, 0.5);
            temp.blendMode = BLEND_MODES.ADD;
            temp.scale = SanEngineManager.dim.scale;
            temp.zIndex = 1;
            return temp;
        }
    }

    public get tlWin():gsap.core.Timeline{
        return this._timeLine!;
    }

    public isSpecial(value: number): void {
        if(!this.multies){
            this.multies = this.addChild(new SymbolMultiplyCounter(value));
            this.multies.position.x = this.symbolWidth/(value >= 10 ? 4 : 3.25);
            if(this.sizeType.value != 1)
                this.multies.position.y = this.symbolHeight/(this.sizeType.value == 4 ? 3 : 4);

            this.multies.zIndex = 3000;
            this.sortableChildren = true;
        }
    }

    Play(lvl: number, durr: number = 0.25): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(this._timeLine, {
            onStart: () => { 
                this.visible = true;
            },
                onComplete:() => {
                    this.visible = false;
                }
        }, true);

        this.seq_glows.setLvl(lvl);
        this.seq_glows.scale.set(1, this.glowSizeY);
        this._timeLine.add(this.seq_glows.play({onStart:() => this.seq_glows.visible = true}));
        this._timeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: durr/2 }, 0);
        this._timeLine.to(this, { alpha: 0, duration: durr/2 }, durr/2);

        return this._timeLine;
    }

    BigWinStart(durr: number = 0.25): gsap.core.Timeline {

        this._timeLine = Helper.setTimeline(this._timeLine, {
            onStart: () => { 
                this.visible = true; 
            },
        }, true);

        this.seq_glows.scale.set(1, this.glowSizeY)
        this.seq_glows.setLvl(3);
        this._timeLine.add(this.seq_glows.play({onStart:() => this.seq_glows.visible = true}));
        this._timeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: durr }, 0);

        return this._timeLine;
    }

    BigWinStop(durr: number = 0.25): gsap.core.Timeline {

        this._timeLine = Helper.setTimeline(this._timeLine, {
            onComplete:() => { 
                this.visible = false;
             }
        }, true);

        this._timeLine.fromTo(this, { alpha: 1 }, { alpha: 0, duration: durr }, 0);
        return this._timeLine;
    }

    Stop(duration: number):gsap.core.Timeline {
        if(this._idleGlow){
            this._idleGlow.timeLine.kill();
            this._idleGlow.destroy();
            this._idleGlow = undefined!;
        }
        this._timeLine = Helper.setTimeline(this._timeLine, { onComplete: () => { this.visible = false; } }, true);
        this._timeLine.to(this, { alpha: 0, duration: duration }, 0);
        return this._timeLine;
    }
    _idleGlow:SpinePlayer;
    Idle(durr: number, texture:PIXI.Texture) {
        this._timeLine = Helper.setTimeline(this._timeLine, {
            onStart: () => { this.visible = true;
                this._mainSprite.visible = true;
                this.alpha = 1;
            },
            onComplete: () => {this.visible = false;
                this._mainSprite.visible = false;
                this.alpha = 0;
            }
        }, true);
        // this._mainSprite.setTexture(texture);
        if(this.seq_glows) this.seq_glows.visible = false;
        this._timeLine.to(this, { duration: durr });

        if(this.isHighValSym && !this._idleGlow){
            this._idleGlow = this.addChild(SanQuentin2.overlay.addChild(new SpinePlayer("MPs")));
            this._idleGlow.zIndex = 100;
            this._idleGlow.play({animation:"animation",skinType:this.symName+"_"+this.sizeType.value});
        }else if(this._idleGlow){
            this._idleGlow.visible = true;
            this._idleGlow.play({animation:"animation",skinType:this.symName+"_"+this.sizeType.value});
        }
    }

    remove(): void {
        if (this._timeLine) this._timeLine.totalProgress(1);
        this.destroy({ children: true });
    }
}